import { useMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { useParams } from "react-router-dom";
import Permissions from "../configs/permissions";
import usePermissions from "../hooks/permissions";
import { QUESTION_MODEL_NAME, QUESTION_TYPES } from "./../configs/constants";
import { createQuestionMutation } from "./../graphql/mutations/vabooSurveyMutations";
import useBaseFieldSetters from "./../hooks/baseFieldSetters";
import useBaseNavigation from "./../hooks/baseNavigation";
import useFormErrors from "./../hooks/formErrors";
import useHyperState from "./../hooks/hyperState";
import { useQuestionFieldSetters } from "./../hooks/vabooHooks";

import {
  AddButton,
  CancelButton,
  DeleteButton,
  FormControls,
  SaveButton,
} from "./../mood-ui/Controls";
import {
  TableContainer,
  TableView,
  TColBorderless,
  TContent,
  THCol,
  THeading,
  TRow,
} from "./../mood-ui/DataTable";
import {
  NumberField,
  SelectField,
  TextField,
  ToggleCheckboxField,
} from "./../mood-ui/Fields";
import {
  BlockContainer,
  Col,
  FormContainer,
  FormData,
  PageContainer,
  Row,
} from "./../mood-ui/Layout";
import { BoxedMessage, Heading } from "./../mood-ui/Typography";

const CREATE_QUESTION = gql(createQuestionMutation);

const QuestionCreate = () => {
  // ===== ЛОГІКА (без змін) =====
  const { protectPage } = usePermissions();
  protectPage(Permissions.Mutation.CreateQuestion);

  const { fieldError, resolveMutationErrors, clearError } = useFormErrors();
  const { goBack } = useBaseNavigation(QUESTION_MODEL_NAME);
  const { id } = useParams();

  const {
    state: formData,
    setState: setFormData,
    stateMethods,
  } = useHyperState("formData", {
    title: "",
    admin_title: "",
    type: "MULTIPLE",
    is_survey_type: true,
    is_profile_type: false,
    answers: [],
  });

  const {
    setFieldValue: handleFieldOnChange,
    toggleCheckbox: handleToggleCheckbox,
  } = useBaseFieldSetters(formData, stateMethods, clearError);

  const {
    handleAddAnswer,
    handleUpdateAnswer,
    handleDeleteAnswer,
    handleUpdateType,
    handleAddDateBracketsAnswer,
    handleUpdateDateBracketsAnswerProperty,
  } = useQuestionFieldSetters(formData, stateMethods);

  const [
    createQuestion,
    { data: createResult, loading: isCreating, error: creationError },
  ] = useMutation(CREATE_QUESTION, {
    variables: { ...formData },
    onCompleted: () => {
      goBack();
    },
    onError: (e) => {
      resolveMutationErrors(e);
    },
  });

  const handleSave = () => {
    const data = {
      ...formData,
      answers: JSON.stringify(formData.answers),
      minimum: parseInt(formData.minimum),
      maximum: parseInt(formData.maximum),
      step: parseInt(formData.step),
      default: parseInt(formData.default),
    };

    console.log(data);
    createQuestion({ variables: data });
  };

  if (!formData) return <div />;

  return (
    <PageContainer>
      <BlockContainer raised>
        <Row tweaks="border-b border-[#dfe2e8] pb-8 mt-0 mx-5">
          <Col width={12}>
            <Heading
              inset
              text="Create a question"
              description="Set up the question wording, choose its answer type, and configure options."
            />
          </Col>
        </Row>

        <FormContainer>
          <FormData>
            <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
              <Col width={3}>
                <Heading
                  size="md"
                  text="Question details"
                  description="Core properties and internal naming."
                />
              </Col>
              <Col
                width={1}
                extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
              />
              <Col width={8}>
                <Row>
                  <Col width={8}>
                    <TextField
                      name="title"
                      label="Question"
                      value={formData.title}
                      onChange={handleFieldOnChange}
                      error={fieldError("title")}
                      isRequired
                      minlength={3}
                      maxlength={160}
                      description="End-user visible wording"
                    />
                  </Col>
                  <Col width={4}>
                    <SelectField
                      value={formData.type}
                      values={QUESTION_TYPES}
                      label="Type"
                      name="type"
                      onChange={handleUpdateType}
                      valueKey="id"
                      labelKey="name"
                      error={fieldError("type")}
                      isRequired
                      description="Answer input format"
                    />
                  </Col>
                  <Col width={12}>
                    <TextField
                      name="admin_title"
                      label="Admin Title"
                      value={formData.admin_title}
                      onChange={handleFieldOnChange}
                      error={fieldError("admin_title")}
                      isRequired
                      maxlength={160}
                      description="Internal name for admin/reporting"
                    />
                  </Col>
                </Row>
              </Col>
            </Row>

            <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
              <Col width={3}>
                <Heading
                  size="md"
                  text="Usage"
                  description="Where this question can be reused."
                />
              </Col>
              <Col
                width={1}
                extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
              />
              <Col width={8}>
                <Row tweaks="gap-0 sm:grid-cols-12 py-1 px-px">
                  <Col width={3} extraStyles="sm:col-span-2">
                    <ToggleCheckboxField
                      id="is_survey_type"
                      name="is_survey_type"
                      label="Survey Usage"
                      description="Available for surveys"
                      isChecked={formData.is_survey_type}
                      onChange={handleToggleCheckbox}
                      flush
                    />
                  </Col>
                  <Col width={3} extraStyles="sm:col-span-2">
                    <ToggleCheckboxField
                      id="is_profile_type"
                      name="is_profile_type"
                      label="Profile Usage"
                      description="Available for profiles"
                      isChecked={formData.is_profile_type}
                      onChange={handleToggleCheckbox}
                      flush
                    />
                  </Col>
                </Row>
              </Col>
            </Row>

            {["TEXT"].indexOf(formData.type) > -1 && (
              <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
                <Col width={3}>
                  <Heading size="md" text="Answers" description="Information" />
                </Col>
                <Col
                  width={1}
                  extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
                />
                <Col width={8}>
                  <BoxedMessage
                    text="Answers are not available for this type"
                    description="When using Text as question type, answers become unavailable."
                  />
                </Col>
              </Row>
            )}

            {["RATING"].indexOf(formData.type) > -1 && (
              <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
                <Col width={3}>
                  <Heading
                    size="md"
                    text="Rating configuration"
                    description="Min/max, step and default rating."
                  />
                </Col>
                <Col
                  width={1}
                  extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
                />
                <Col width={8}>
                  <Row>
                    <Col width={3}>
                      <NumberField
                        name="minimum"
                        label="Minimum Rating Value"
                        value={formData.minimum}
                        onChange={(e) => handleFieldOnChange(e, "int")}
                        error={fieldError("minimum")}
                        description="Lowest allowed value"
                      />
                    </Col>
                    <Col width={3}>
                      <NumberField
                        name="maximum"
                        label="Maximum Rating Value"
                        value={formData.maximum}
                        onChange={(e) => handleFieldOnChange(e, "int")}
                        error={fieldError("maximum")}
                        description="Highest allowed value"
                      />
                    </Col>
                    <Col width={3}>
                      <NumberField
                        name="step"
                        label="Rating Step"
                        value={formData.step}
                        onChange={(e) => handleFieldOnChange(e, "int")}
                        error={fieldError("step")}
                        description="Increment"
                      />
                    </Col>
                    <Col width={3}>
                      <NumberField
                        name="default"
                        label="Default Value"
                        value={formData.default}
                        onChange={(e) => handleFieldOnChange(e, "int")}
                        error={fieldError("default")}
                        description="Preselected rating"
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            )}

            {["YEAR"].indexOf(formData.type) > -1 && (
              <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
                <Col width={3}>
                  <Heading
                    size="md"
                    text="Year range"
                    description="Numeric bounds and step."
                  />
                </Col>
                <Col
                  width={1}
                  extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
                />
                <Col width={8}>
                  <Row>
                    <Col width={3}>
                      <NumberField
                        name="minimum"
                        label="Year (From)"
                        value={formData.minimum}
                        onChange={handleFieldOnChange}
                        error={fieldError("minimum")}
                        description="Lower bound"
                      />
                    </Col>
                    <Col width={3}>
                      <NumberField
                        name="maximum"
                        label="Year (To)"
                        value={formData.maximum}
                        onChange={handleFieldOnChange}
                        error={fieldError("maximum")}
                        description="Upper bound"
                      />
                    </Col>
                    <Col width={3}>
                      <NumberField
                        name="step"
                        label="Year Step"
                        value={formData.step}
                        onChange={handleFieldOnChange}
                        error={fieldError("step")}
                        description="Increment"
                      />
                    </Col>
                    <Col width={3}>
                      <NumberField
                        name="default"
                        label="Default Value"
                        value={formData.default}
                        onChange={handleFieldOnChange}
                        error={fieldError("default")}
                        description="Preselected year"
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            )}

            {["DATE_BRACKETS"].indexOf(formData.type) > -1 && (
              <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
                <Col width={3}>
                  <Heading
                    size="md"
                    text="Answers"
                    description="Add ranges and their weights."
                  />
                </Col>
                <Col
                  width={1}
                  extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
                />
                <Col width={8}>
                  <Row>
                    <Col width={12} extraStyles="flex items-center gap-2 mb-3">
                      <AddButton icon onClick={handleAddDateBracketsAnswer} />
                      <span className="text-sm text-[#6b7280]">Add Answer</span>
                    </Col>
                  </Row>
                  {formData.answers.length > 0 && (
                    <TableContainer tweaks="relative mt-0">
                      <TableView>
                        <THeading>
                          <TRow>
                            <THCol name="minimum" value="Range (From)" />
                            <THCol name="maximum" value="Range (To)" />
                            <THCol name="title" value="Range (Text)" />
                            <THCol name="weight" value="Computed Weight" />
                            <THCol />
                          </TRow>
                        </THeading>
                        <TContent>
                          {formData.answers.map((answer, index) => (
                            <TRow key={index}>
                              <TColBorderless tweaks="align-middle">
                                <NumberField
                                  value={answer.minimum}
                                  onChange={(e) =>
                                    handleUpdateDateBracketsAnswerProperty(
                                      index,
                                      "minimum",
                                      e.target.value
                                    )
                                  }
                                />
                              </TColBorderless>
                              <TColBorderless tweaks="align-middle">
                                <NumberField
                                  value={answer.maximum}
                                  onChange={(e) =>
                                    handleUpdateDateBracketsAnswerProperty(
                                      index,
                                      "maximum",
                                      e.target.value
                                    )
                                  }
                                />
                              </TColBorderless>
                              <TColBorderless tweaks="align-middle">
                                <TextField
                                  value={answer.title}
                                  onChange={(e) =>
                                    handleUpdateDateBracketsAnswerProperty(
                                      index,
                                      "title",
                                      e.target.value
                                    )
                                  }
                                />
                              </TColBorderless>
                              <TColBorderless tweaks="align-middle">
                                <NumberField
                                  value={answer.weight}
                                  onChange={(e) =>
                                    handleUpdateDateBracketsAnswerProperty(
                                      index,
                                      "weight",
                                      e.target.value
                                    )
                                  }
                                />
                              </TColBorderless>
                              <TColBorderless
                                width="w-12"
                                tweaks="px-0.5 py-0.5 align-middle text-right"
                              >
                                <DeleteButton
                                  icon
                                  onClick={() => handleDeleteAnswer(index)}
                                />
                              </TColBorderless>
                            </TRow>
                          ))}
                        </TContent>
                      </TableView>
                    </TableContainer>
                  )}
                </Col>
              </Row>
            )}

            {[
              "MULTIPLE",
              "MULTIPLE_AND_CUSTOM",
              "SINGLE",
              "SINGLE_AND_CUSTOM",
            ].indexOf(formData.type) > -1 && (
              <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
                <Col width={3}>
                  <Heading
                    size="md"
                    text="Answers"
                    description="Provide selectable options."
                  />
                </Col>
                <Col
                  width={1}
                  extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
                />
                <Col width={8}>
                  <Row>
                    <Col width={12} extraStyles="flex items-center gap-2 mb-3">
                      <AddButton icon onClick={handleAddAnswer} />
                      <span className="text-sm text-[#6b7280]">Add Answer</span>
                    </Col>
                  </Row>

                  {formData.answers.length > 0 && (
                    <TableContainer tweaks="relative mt-0">
                      <TableView>
                        <THeading>
                          <TRow>
                            <THCol name="title" />
                            <THCol />
                          </TRow>
                        </THeading>
                        <TContent>
                          {formData.answers.map((answer, index) => (
                            <TRow key={index}>
                              <TColBorderless tweaks="align-middle">
                                <TextField
                                  name={`title_${index}`}
                                  value={answer.title}
                                  onChange={handleUpdateAnswer}
                                />
                              </TColBorderless>
                              <TColBorderless
                                width="w-12"
                                tweaks="px-0.5 py-0.5 align-middle text-right"
                              >
                                <DeleteButton
                                  icon
                                  onClick={() => handleDeleteAnswer(index)}
                                />
                              </TColBorderless>
                            </TRow>
                          ))}
                        </TContent>
                      </TableView>
                    </TableContainer>
                  )}
                </Col>
              </Row>
            )}
          </FormData>

          <FormControls>
            <CancelButton onClick={goBack} />
            <SaveButton onClick={handleSave} loading={isCreating} />
          </FormControls>
        </FormContainer>
      </BlockContainer>
    </PageContainer>
  );
};

export default QuestionCreate;
